/**
 * The vocabulary of the command system. Three things are deliberately kept
 * apart in three different files, and this one only names them:
 *
 *   1. a COMMAND — what the app can do, plus how to describe it to a user
 *      (`catalog.ts`). Carries no keys.
 *   2. a BINDING — which chord or sequence invokes it (`keymap.ts` for the
 *      shipped defaults, `keymapStorage.ts` for the user's overrides).
 *   3. a HANDLER — the function that actually runs, supplied by whichever
 *      component owns the behaviour (`useCommandHandler`).
 *
 * The split is the point. A command's identity outlives its chord, so a user
 * rebinding "reset the camera" changes one entry in a persisted override map
 * and nothing else; and a command with no mounted handler simply does not fire,
 * which is what lets scopes mean something.
 */
// Every hotkeys import in this app goes through the REACT adapter, never
// `@tanstack/hotkeys` directly, even for the pure helpers and types that live
// in the core package — `@tanstack/react-hotkeys` re-exports all of it
// (`export * from '@tanstack/hotkeys'`). The core package is a SINGLETON: one
// `HotkeyManager`, one `KeyStateTracker`. Depending on it directly invites pnpm
// to resolve a second copy the day the adapter's range moves, and two managers
// means half the app's shortcuts silently stop firing. One import path, one
// copy.
import type { Hotkey, HotkeySequence } from '@tanstack/react-hotkeys';
import type { ParseKeys } from 'i18next';

/**
 * Where a command is allowed to fire.
 *
 * TanStack Hotkeys has no scope concept of its own — its only scoping tool is
 * `target`, a DOM element — so scopes are this layer's. `'global'` is always
 * active; every other scope is pushed by `useCommandScope` while the surface
 * that owns it is mounted, and popped when it unmounts.
 *
 * Two rules, and the second is the one that is easy to get wrong:
 *
 * - **A leaf scope dominates `'global'` on the same chord**, and that is
 *   intended: it is how `Escape` closes the design-point detail pane rather
 *   than whatever `Escape` means at the top level. `findKeymapConflicts` does
 *   not report it.
 * - **Two leaf scopes CAN be active at once.** They are not mutually
 *   exclusive, however tempting that assumption is: on `/simops/$studyId`
 *   the writeup drawer keeps its Lexical editor mounted for the whole route by
 *   design (see `WriteupDrawer`'s "stays mounted for the whole route"), so
 *   `reportWriteup` sits on the stack alongside `designPoints` or `viewer` on
 *   the app's most-used screen. `resolveBindings` would arbitrate a chord
 *   shared between two co-active leaves by MOUNT ORDER, which is not something
 *   a user could predict — so `findKeymapConflicts` reports that pair instead,
 *   and the recorder refuses to create one silently.
 */
export type CommandScope =
  | 'global'
  | 'viewer'
  | 'designPoints'
  | 'reportWriteup';

/** Groups the shortcuts panel renders as sections. Display only. */
export type CommandGroup = 'general' | 'navigation' | 'viewer' | 'editing';

/**
 * The platforms `Mod` resolves against, and the only place this union is
 * spelled out — `keymap.ts`, `resolve.ts` and `display.ts` all import it. It
 * was declared separately in each of those, which is three copies to drift the
 * day the union gains a member.
 */
export type Platform = 'mac' | 'windows' | 'linux';

/**
 * One invocation: either a single chord (`'Mod+S'`) or a Vim-style sequence
 * (`['G', 'H']`). An array is a sequence — see `isSequenceBinding`.
 *
 * Prefer `Mod+` over `Control+`/`Meta+` in anything shipped. `Mod` resolves to
 * Command on macOS and Control everywhere else, so one binding is correct on
 * every platform and `formatShortcut` renders it the way that platform's users
 * expect it to be written.
 */
export type Binding = Hotkey | HotkeySequence;

/** Narrows a binding to its sequence form. */
export function isSequenceBinding(binding: Binding): binding is HotkeySequence {
  return Array.isArray(binding);
}

/**
 * An i18n key, checked against the English resources.
 *
 * `ParseKeys` is what `t()` itself accepts (see `src/i18n/i18next.d.ts`), so a
 * typo in a catalog entry is a compile error here rather than a raw
 * `commands.viewer.resetCmaera` rendered in the shortcuts dialog. The import is
 * type-only and erased, so the catalog stays a plain data module at runtime —
 * importable from a node script without pulling in i18next.
 */
export type TranslationKey = ParseKeys;

/**
 * A command: what it does and how to talk about it. Never how to press it.
 *
 * `titleKey`/`descriptionKey` are i18n keys rather than finished strings, so
 * the catalog carries no locale and the same entry renders in en, ja and
 * zh-TW.
 */
export interface Command {
  readonly id: CommandId;
  /** i18n key for the command's name, e.g. `commands.viewer.resetCamera`. */
  readonly titleKey: TranslationKey;
  /** i18n key for the one-line explanation shown in the shortcuts panel. */
  readonly descriptionKey?: TranslationKey;
  readonly group: CommandGroup;
  /** Defaults to `'global'`. */
  readonly scope?: CommandScope;
  /**
   * Per-command overrides for the hotkey options this layer would otherwise
   * infer. Omit unless the command genuinely needs it — the inferred defaults
   * are documented on `bindingOptions`.
   */
  readonly options?: CommandKeyOptions;
}

/**
 * The subset of TanStack's `HotkeyOptions` a command may pin.
 *
 * `enabled`, `target`, `platform` and `meta` are deliberately absent: enabling
 * is decided per-render by scope and handler presence, `target` is what scopes
 * replace, `platform` is detected, and `meta` is filled from the catalog.
 */
export interface CommandKeyOptions {
  /**
   * Let the chord fire while focus is inside a text input, textarea, select or
   * contenteditable. TanStack's own default is derived from the chord's shape
   * (a bare letter is ignored in inputs; a Ctrl/Meta chord is not), which is
   * almost always what you want — set this only to overrule that inference.
   */
  readonly ignoreInputs?: boolean;
  readonly preventDefault?: boolean;
  readonly stopPropagation?: boolean;
  readonly eventType?: 'keydown' | 'keyup';
  /** Fire once until every key is released. For held-key commands. */
  readonly requireReset?: boolean;
  /** Sequence bindings only: ms allowed between steps. */
  readonly sequenceTimeout?: number;
}

/**
 * Every command id in the app.
 *
 * Spelled out as a union rather than inferred from the catalog so that the id
 * is a type the whole app can name — `useCommandHandler('viewer.resetCamera')`
 * is checked at the call site, a typo in the catalog is a compile error, and
 * `DEFAULT_KEYMAP` can be a total `Record<CommandId, …>` that fails to build
 * the moment a command is added without a default binding.
 */
export type CommandId =
  // General
  | 'general.showShortcuts'
  | 'general.toggleAssistant'
  | 'general.closeOverlay'
  // Navigation (sequences)
  | 'navigation.goHome'
  | 'navigation.goMeshgen'
  | 'navigation.goSimops'
  // Viewer
  //
  // Only what the renderer actually exposes. Axes and the cutaway view are
  // store-only fields the agent seeds one-way (ENG-882: viewer interaction is
  // deliberately open-loop, with no renderer→store writeback), so a command
  // toggling them would report a state the frame does not have; and a
  // user-facing screenshot needs a real download path, not
  // `captureViewportScreenshot`, which returns a 720px JPEG for agent context.
  // Both are worth adding — with the plumbing, not with a catalog entry.
  //
  // A wireframe toggle is absent for a different reason: the renderer ALREADY
  // has one, on `f`, in its own canvas `keyup` handler. A command bound to some
  // other key would give one action two shortcuts, only one of which appears in
  // the shortcuts dialog and only one of which is rebindable — exactly the
  // confusion `reservedKeys.ts` exists to prevent. Moving the renderer's keys
  // into the catalog is the fix, and it is a bigger change than this.
  | 'viewer.resetCamera'
  // Editing / list surfaces
  | 'designPoints.selectNext'
  | 'designPoints.selectPrevious'
  | 'designPoints.closeDetail'
  | 'reportWriteup.save';

/**
 * A complete binding table: every command, bound.
 *
 * One type rather than the `DefaultKeymap`/`ResolvedKeymap` pair it replaces.
 * Those were structurally identical, so every function taking one accepted the
 * other and the distinction was prose pretending to be a type — a reader had
 * to check to learn they were interchangeable.
 *
 * Total on purpose: `DEFAULT_KEYMAP` is one of these, so adding a command
 * without a default binding fails the build. An unbound command is invisible in
 * the shortcuts panel and impossible to discover.
 */
export type Keymap = Readonly<Record<CommandId, readonly Binding[]>>;

/**
 * A user's customizations. Sparse — an absent id means "use the default", and
 * an explicitly empty array means "the user unbound this", which is a
 * different thing and must survive a round trip through storage.
 */
export type KeymapOverrides = Partial<Keymap>;
